import React, { useEffect, useMemo, useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import { Stack, Button, Divider, Box, Typography } from '@mui/material_modern'
import Loading from 'components/generic/Loading'

// components
import SubFormLayout from 'components/layouts/SubFormLayout'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import FileUploader from 'components/generic/file-uploader/FileUploader'

// utils
import { DISPATCH_TICKET_ENUM, DISPATCH_TICKET_STATUSES, DT_STATUS_ENUM, GS_ERROR_CODE } from 'utils/constants'
import { SUBMIT_FORM_TYPES } from 'components/containers/load-board/utils/constants'
import { useDispatchTicket } from 'components/containers/load-board/utils/useDispatchTicket'
import { getDayHourSecondString } from 'components/containers/load-board/utils/helperFunctions'
import { dispatchSnackbar } from 'utils/helpers'
import { setSnackBar } from 'state/ui/index'

const areTimeStampsValid = data => {
  const timeStamps = [
    data.startTime || undefined,
    data.yardDepartureDate || undefined,
    data.customerArrivalDate || undefined,
    data.customerDepartureDate || undefined,
    data.yardArrivalDate || undefined,
    data.yardReturnDepartureDate || undefined,
    data.customerReturnArrivalDate || undefined,
    data.customerReturnDepartureDate || undefined,
    data.yardReturnArrivalDate || undefined,
    data.completionTime || undefined,
  ]

  for (let i = 1; i < timeStamps.length; i++) {
    if (timeStamps[i] !== undefined && timeStamps[i - 1] !== undefined) {
      const current = new Date(timeStamps[i])
      const prev = new Date(timeStamps[i - 1])

      // Set milliseconds to 0 for both (irrelevant)
      current.setMilliseconds(0)
      prev.setMilliseconds(0)

      if (current < prev) {
        return false
      }
    }
  }
  return true
}

const DispatchTicketStatusBlade = ({ onFormStateChange, onHideModal }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { type, dispatchTicketId, startTime } = useSelector(state => state.ui.formModalProps)
  const methods = useForm()
  const { setValue, handleSubmit, watch } = methods
  const currentStartDate = new Date(watch('startDate'))
  const currentStartTime = new Date(watch('startTime'))
  const currentCompletionTime = new Date(watch('completionTime'))
  const currentDocs = watch('docs')
  const [isLoadingResponse, setIsLoadingResponse] = useState(false)

  const { onSubmitDispatchTicket, dispatchTicketData } = useDispatchTicket({
    dispatchTicketId,
    methods,
  })

  useEffect(() => {
    if (startTime) {
      setValue('startDate', new Date(startTime))
      setValue('startTime', new Date(startTime))
      setValue(
        'yardDepartureDate',
        dispatchTicketData?.yardDepartureDate ? new Date(dispatchTicketData.yardDepartureDate) : null,
      )
      setValue(
        'customerArrivalDate',
        dispatchTicketData?.customerArrivalDate ? new Date(dispatchTicketData.customerArrivalDate) : null,
      )
      setValue(
        'customerDepartureDate',
        dispatchTicketData?.customerDepartureDate ? new Date(dispatchTicketData.customerDepartureDate) : null,
      )
      setValue(
        'yardArrivalDate',
        dispatchTicketData?.yardArrivalDate ? new Date(dispatchTicketData.yardArrivalDate) : null,
      )
      setValue(
        'yardReturnDepartureDate',
        dispatchTicketData?.yardReturnDepartureDate ? new Date(dispatchTicketData.yardReturnDepartureDate) : null,
      )
      setValue(
        'customerReturnArrivalDate',
        dispatchTicketData?.customerReturnArrivalDate ? new Date(dispatchTicketData.customerReturnArrivalDate) : null,
      )
      setValue(
        'customerReturnDepartureDate',
        dispatchTicketData?.customerReturnDepartureDate
          ? new Date(dispatchTicketData.customerReturnDepartureDate)
          : null,
      )
      setValue(
        'yardReturnArrivalDate',
        dispatchTicketData.yardReturnArrivalDate ? new Date(dispatchTicketData.yardReturnArrivalDate) : null,
      )

      // Always use existing completion time if it exists, otherwise default to now
      const startingCompletionTime = dispatchTicketData?.completionTime
        ? new Date(dispatchTicketData.completionTime)
        : new Date()

      setValue('completionTime', startingCompletionTime)
    }
  }, [startTime])

  useEffect(() => {
    if (dispatchTicketData) {
      setValue('docs', dispatchTicketData.docs)
    }
  }, [dispatchTicketData])

  useEffect(() => {
    const durationString = getDayHourSecondString(currentStartTime, currentCompletionTime)
    setValue('duration', durationString)
  }, [currentStartTime, currentCompletionTime])

  useEffect(() => {
    onFormStateChange(false)
  }, [])

  const handleSuccess = () => {
    dispatchSnackbar(dispatch, 'success', 'Ticket successfully updated.')
    onHideModal()
  }

  const handleError = error => {
    let errorMessage = `There was an error updating the ticket to ${type}.`

    if (error?.data?.code === GS_ERROR_CODE.CONCURRENCY_VIOLATION) {
      errorMessage = `Dispatch ticket has been updated by another user. Please refresh your page and try again.`
    }

    dispatchSnackbar(dispatch, 'error', errorMessage)

    setIsLoadingResponse(false)
  }

  const onUploadFiles = useCallback(uploadcareGroupId => {
    onSubmitDispatchTicket(
      { uploadcareGroupId },
      {
        handleSuccess: () => dispatchSnackbar(dispatch, 'success', 'Attachments successfully saved.'),
        handleError: error => {
          let errorMessage = 'There was an error saving your attachments.'

          if (error?.data?.code === GS_ERROR_CODE.CONCURRENCY_VIOLATION) {
            errorMessage = `Dispatch ticket has been updated by another user. Please refresh your page and try again.`
          }

          dispatchSnackbar(dispatch, 'error', errorMessage)
        },
        submitFormType: SUBMIT_FORM_TYPES.ATTACHMENTS,
      },
    )
  }, [])
  const onRemoveFiles = docs => {
    onSubmitDispatchTicket(
      { docs },
      {
        handleSuccess: () => dispatchSnackbar(dispatch, 'success', 'Attachment successfully removed'),
        handleError: error => {
          let errorMessage = 'There was an error removing your attachment.'

          if (error?.data?.code === GS_ERROR_CODE.CONCURRENCY_VIOLATION) {
            errorMessage = `Dispatch ticket has been updated by another user. Please refresh your page and try again.`
          }

          dispatchSnackbar(dispatch, 'error', errorMessage)
        },
        submitFormType: SUBMIT_FORM_TYPES.ATTACHMENTS,
      },
    )
  }

  const fields = useMemo(() => {
    const fields = [
      [
        {
          fieldType: 'custom',
          customComponent: () => (
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Timeline
            </Typography>
          ),
        },
      ],
      [
        {
          name: 'startTime',
          label: 'Start Date',
          dataTest: 'start-time',
          fieldType: 'dateTime',
        },
      ],
      [
        {
          name: 'yardDepartureDate',
          label: 'Yard Departure Date',
          dataTest: 'yard-departure-date',
          fieldType: 'dateTime',
          defaultValue: '',
        },
      ],
      [
        {
          name: 'customerArrivalDate',
          label: 'Customer Arrival Date',
          dataTest: 'customer-arrival-date',
          fieldType: 'dateTime',
          defaultValue: '',
        },
      ],
      [
        {
          name: 'customerDepartureDate',
          label: 'Customer Departure Date',
          dataTest: 'customer-departure-date',
          fieldType: 'dateTime',
          defaultValue: '',
        },
      ],
      [
        {
          name: 'yardArrivalDate',
          label: 'Yard Arrival Date',
          dataTest: 'yard-arrival-date',
          fieldType: 'dateTime',
          defaultValue: '',
        },
      ],
    ]

    if (dispatchTicketData?.type === DISPATCH_TICKET_ENUM.PICK_AND_RETURN) {
      fields.push(
        [
          {
            name: 'yardReturnDepartureDate',
            label: 'Yard Return Departure Date',
            dataTest: 'yard-return-departure-date',
            fieldType: 'dateTime',
            defaultValue: '',
          },
        ],
        [
          {
            name: 'customerReturnArrivalDate',
            label: 'Customer Return Arrival Date',
            dataTest: 'customer-return-arrival-date',
            fieldType: 'dateTime',
            defaultValue: '',
          },
        ],
        [
          {
            name: 'customerReturnDepartureDate',
            label: 'Customer Return Departure Date',
            dataTest: 'customer-return-departure-date',
            fieldType: 'dateTime',
            defaultValue: '',
          },
        ],
        [
          {
            name: 'yardReturnArrivalDate',
            label: 'Yard Return Arrival Date',
            dataTest: 'yard-return-arrival-date',
            fieldType: 'dateTime',
            defaultValue: '',
          },
        ],
      )
    }

    fields.push(
      [
        {
          name: 'completionTime',
          label: 'Completion Date',
          dataTest: 'completion-time',
          fieldType: 'dateTime',
          defaultValue: '',
        },
      ],
      [
        {
          name: 'duration',
          label: 'Total Duration',
          dataTest: 'total-duration',
          fieldType: 'static',
          defaultValue: '',
        },
      ],
      [
        {
          fieldType: 'custom',
          customComponent: () => (
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Details
            </Typography>
          ),
        },
      ],
      [
        {
          name: 'mileage',
          label: 'Mileage',
          dataTest: 'mileage',
          fieldType: 'input',
          InputProps: {
            inputComponent: InputFormat,
            inputProps: {
              inputType: 'number',
            },
          },
          defaultValue: dispatchTicketData?.mileage || '',
          sx: { width: '50%' },
        },
      ],
      [
        {
          name: 'driverCompletionNotes',
          label: 'Driver Notes',
          dataTest: 'driver-note',
          fieldType: 'input',
          defaultValue: dispatchTicketData?.driverCompletionNotes || '',
          multiline: true,
          minRows: 1,
          maxRows: 10,
        },
      ],
      [
        {
          name: 'dispatcherCompletionNotes',
          label: 'Dispatcher Notes',
          dataTest: 'dispatcher-note',
          fieldType: 'input',
          defaultValue: dispatchTicketData?.dispatcherCompletionNotes || '',
          multiline: true,
          minRows: 1,
          maxRows: 10,
        },
      ],
    )

    return fields
  }, [])

  if (isLoadingResponse) return <Loading localized />

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(data => {
          if (!areTimeStampsValid(data)) {
            dispatch(
              setSnackBar({
                snackbarType: 'error',
                snackbarMessage: 'Failed to complete Dispatch Ticket. Dates are not in chronological order.',
              }),
            )
          } else {
            setIsLoadingResponse(true)
            onSubmitDispatchTicket(data, {
              handleSuccess,
              handleError,
              status: type,
              submitFormType: SUBMIT_FORM_TYPES.BLADE,
            })
          }
        })}
      >
        <Stack height="100vh" width="100%">
          <Stack>
            <FormModalHeading
              title={`Mark Ticket #${dispatchTicketData?.externalId} as ${DISPATCH_TICKET_STATUSES[type]?.name}`}
              backButtonPosition="right"
              onBack={() => onHideModal(navigate)}
            />
            <Divider />
          </Stack>

          <Stack flex={1} overflow="auto">
            <SubFormLayout
              formName=""
              boxStyles={{ width: 'unset', backgroundColor: 'unset', mx: 2 }}
              fields={fields}
            />
            <Box p={2}>
              <FileUploader
                onUploadFiles={onUploadFiles}
                onRemoveFiles={onRemoveFiles}
                filesData={currentDocs || []}
                tooltipMsg=""
                buttonText="Add Attachments"
              />
            </Box>
          </Stack>
          <Divider />
          <Stack direction="row" p={2} spacing={2} alignItems="center" mx={2}>
            <Button
              type="submit"
              variant="contained"
              color={type === DISPATCH_TICKET_STATUSES.COMPLETED.id ? 'primary' : 'error'}
            >
              {dispatchTicketData.status === DT_STATUS_ENUM.COMPLETED ||
              dispatchTicketData.status === DT_STATUS_ENUM.FAILED
                ? 'Save'
                : 'Mark as ' + type}
            </Button>
            <Button variant="outlined" color="primary" onClick={onHideModal}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </form>
    </FormProvider>
  )
}

DispatchTicketStatusBlade.propTypes = {
  onHideModal: PropTypes.func,
  onFormStateChange: PropTypes.func,
}

DispatchTicketStatusBlade.defaultProps = {
  onHideModal: null,
  onFormStateChange: null,
}

export default DispatchTicketStatusBlade
